import {
  DashboardState,
  EDatasourceType,
  ESdChangeReason,
  EStatementLanguage,
  EUserAccessPart,
  IActionDefinitionDto,
  IActionFolder,
  IDatasourceDto,
  IGenericSelectDataModel,
  isInlineCodeDatasourceType,
  LeftPanelType,
  ViewPart
} from "@kuika/kuika-cl-model";
import _, { endsWith } from "lodash";
import * as React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import { openDatasourceForEditing } from "../../../../shared/utility/designer-view-actions";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import {
  MCP_BACKEND_NOTIFICATION_EVENT,
  consumePendingNotifications,
  type BackendNotificationPayload
} from "../../../../shared/utility/mcp-backend-notifications";
import { prefetchProcessUsedActionIds } from "../../../../shared/utility/process-action-usage";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import { requestDatasourceReveal } from "../../data-diagram/datasource-reveal";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import { ActionUsagesModal } from "./action-usages-modal";
import { FolderCreateComponent } from "./data-source-folder-add";
import { GenericSqlModal } from "./generic-sql-modal";
import { SqlActionModal } from "./sql-action-modal";

const iconMap = {
  [EDatasourceType.ManagedDb]: "database",
  [EDatasourceType.ManagedDbOracle]: "oracle",
  [EDatasourceType.ManagedDbPostgre]: "postgre",
  [EDatasourceType.Postgre]: "postgre",
  [EDatasourceType.CustomDb]: "database",
  [EDatasourceType.Oracle]: "oracle",
  [EDatasourceType.MsSql]: "database",
  [EDatasourceType.RestApi]: "cloud-cog",
  [EDatasourceType.RestApiWOSwagger]: "swagger",
  [EDatasourceType.SAP]: "sap",
  [EDatasourceType.CSharpFunctions]: "hexagon",
  [EDatasourceType.CustomAuthenticationProvider]: "hexagon",
  [EDatasourceType.CustomAuthorizationProvider]: "hexagon",
  [EDatasourceType.CustomVerificationCodeSenderProvider]: "hexagon",
  [EDatasourceType.CustomConnectionStringProvider]: "hexagon",
  [EDatasourceType.CustomExternalTokenValidatorProvider]: "hexagon",
  [EDatasourceType.BleService]: "bluetooth",
  [EDatasourceType.AIActions]: "sparkles",
  [EDatasourceType.AIAgent]: "ai-agents",
  [EDatasourceType.Mqtt]: "mqtt",
  [EDatasourceType.Blockly]: "puzzle",
  [EDatasourceType.JavaScriptAction]: "typescript",
  [EDatasourceType.PythonAction]: "python",
  [EDatasourceType.JavaAction]: "java",
  [EDatasourceType.GoAction]: "go",
  [EDatasourceType.RustAction]: "rust",
  [EDatasourceType.VisionAction]: "eye"
};

// Provider-type C# datasources override PLATFORM behavior (login, authorization, connection
// string, ...). In the list they are indistinguishable from plain C# code unless the user named
// them well — each gets a badge with a short label and a tooltip explaining what it hooks into.
// Descriptions mirror the ACTUAL runtime fallback semantics (KAuthentication, KAuthorization,
// KVerificationCodeSenderProvider, KContext) — only the authorization provider fully replaces
// its platform counterpart; the others are hooks with a platform fallback.
export const providerBadgeMap: { [key: string]: { label: string; description: string } } = {
  [EDatasourceType.CustomAuthenticationProvider]: {
    label: "Auth",
    description:
      "Custom Authentication Provider — runs first on password sign-in AND external-token login (loginwithtoken); returning null falls back to the platform flow."
  },
  [EDatasourceType.CustomAuthorizationProvider]: {
    label: "Authz",
    description: "Custom Authorization Provider — resolves the user's roles instead of the platform's role assignments."
  },
  [EDatasourceType.CustomVerificationCodeSenderProvider]: {
    label: "Verify",
    description:
      "Custom Verification Code Sender — delivers verification codes; returning false falls back to the platform e-mail template."
  },
  [EDatasourceType.CustomConnectionStringProvider]: {
    label: "Conn Str",
    description: "Custom Connection String Provider — no longer supported and has no effect on the app. Delete it."
  },
  [EDatasourceType.CustomExternalTokenValidatorProvider]: {
    label: "SSO",
    description:
      "External Token Validator — validates third-party SSO tokens for its External1..External5 slot, adding an external sign-in path."
  }
};

export const DatasourceProviderBadge = ({ datasource }: { datasource: IDatasourceDto | undefined }) => {
  const providerInfo = datasource ? providerBadgeMap[datasource.datasourceType] : undefined;
  if (!providerInfo || !datasource) {
    return null;
  }
  return (
    <KuikaTooltip content={providerInfo.description}>
      <span
        data-testid={`datasourceProviderBadge-${datasource.name}`}
        style={{ display: "inline-flex", flexShrink: 0 }}
      >
        <KuikaBadge content={providerInfo.label} color="purple" size="small" />
      </span>
    </KuikaTooltip>
  );
};

export interface SchemaNode {
  title: string;
  key: string;
  children: Array<SchemaNode>;
  isLeaf: boolean;
  schema?: string;
  /** Column nodes only — Kuika type name (`Boolean`, `Int32`, …) for the type glyph. */
  kuikaDataType?: string;
}

export interface IDataSourcesState {
  isSearchActive: boolean;
  isCustomActionModalActive: boolean;
  isNewFolderModalActive: boolean;
  isEditFolderModalActive: boolean;
  selectedDsCustomActions: IActionDefinitionDto[];
  activeDatasource: IDatasourceDto;
  dataSourceToEdit: IDatasourceDto;
  actionToEdit: IActionDefinitionDto;
  actionToDelete: IActionDefinitionDto;
  actionToInspectUsages: IActionDefinitionDto | undefined;
  isActionUsagesModalVisible: boolean;
  isDeleteSqlActionModalVisible: boolean;
  folderToDelete: IActionFolder;
  isDeleteActionFolderModalVisible: boolean;
  schema: SchemaNode[];
  searchText: string;
  editingFolderId: string;
  editingFolderName: string;
  activeKeys: string[];
  isDatasourcesLoading: boolean;
  refreshAction: boolean;
  isNewGenericSqlAction: boolean;
  folderParentFolderId: string | null | undefined;
  folderToAdd: string | undefined;
  isMenuOpen: boolean;
  genericSelectData: IGenericSelectDataModel | undefined;
  mcpHighlightActionName: string | null;
  mcpHighlightIsDelete: boolean;
}

export interface IDataSourcesProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

class DataSources extends PureSdComponent<IDataSourcesProps & IWithSDProps & IWithContextProp, IDataSourcesState> {
  constructor(props: IDataSourcesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isSearchActive: false,
      isCustomActionModalActive: false,
      isNewFolderModalActive: false,
      isEditFolderModalActive: false,
      selectedDsCustomActions: [],
      activeDatasource: {} as IDatasourceDto,
      dataSourceToEdit: {} as IDatasourceDto,
      actionToEdit: {} as IActionDefinitionDto,
      actionToDelete: {} as IActionDefinitionDto,
      actionToInspectUsages: undefined,
      isActionUsagesModalVisible: false,
      folderToDelete: {} as IActionFolder,
      isDeleteSqlActionModalVisible: false,
      isDeleteActionFolderModalVisible: false,
      schema: [],
      searchText: "",
      editingFolderId: "",
      editingFolderName: "",
      activeKeys: [],
      isDatasourcesLoading: false,
      refreshAction: false,
      isNewGenericSqlAction: false,
      folderParentFolderId: "",
      folderToAdd: "",
      isMenuOpen: false,
      genericSelectData: undefined,
      mcpHighlightActionName: null,
      mcpHighlightIsDelete: false
    };
  }

  private mcpHighlightTimer: ReturnType<typeof setTimeout> | null = null;

  async componentDidMount() {
    // Warm the "used in a process" cache so the editor warning is ready before an action is opened.
    prefetchProcessUsedActionIds(this.props.sd.project?.id);
    if (!this.props.sd.isActionsLoaded) {
      await this.getDatasourcesOfDomain();
    }
    if (this.props.sd.datasources.length > 0 && !this.state.activeDatasource.id) {
      const managedDb = this.getManagedDB();
      this.selectDatasource(managedDb || this.props.sd.datasources[0]);
    }
    this.handleSearchTextOnInit();

    // MCP backend notification listener
    window.addEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleMcpNotification as EventListener);

    // Check for pending notifications that arrived before this component mounted
    const pending = consumePendingNotifications("panel-datasources");
    if (pending.length > 0) {
      const latest = pending[pending.length - 1];
      this.applyMcpHighlight(latest);
    }
  }

  componentWillUnmount() {
    window.removeEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleMcpNotification as EventListener);
    if (this.mcpHighlightTimer) clearTimeout(this.mcpHighlightTimer);
    this.clearExpandHover();
  }

  handleMcpNotification = (event: CustomEvent<BackendNotificationPayload>) => {
    const payload = event.detail;
    if (payload.target !== "panel-datasources") return;
    this.applyMcpHighlight(payload);
  };

  applyMcpHighlight = (payload: BackendNotificationPayload) => {
    if (this.mcpHighlightTimer) clearTimeout(this.mcpHighlightTimer);

    this.setState({
      mcpHighlightActionName: payload.itemName || null,
      mcpHighlightIsDelete: payload.isDelete
    });

    this.mcpHighlightTimer = setTimeout(() => {
      this.setState({
        mcpHighlightActionName: null,
        mcpHighlightIsDelete: false
      });
      this.mcpHighlightTimer = null;
    }, 4000);
  };

  componentDidUpdate = async (prevProps, prevState) => {
    const actionDto = this.props.sd.actionToEditOnActionPanel;
    if (actionDto !== prevProps.sd.actionToEditOnActionPanel && actionDto) {
      this.handleActionEdit(actionDto);
    }
    if (prevState.isNewFolderModalActive && !this.state.isNewFolderModalActive && this.state.folderParentFolderId) {
      await this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Screens);
      await this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Datasources);
    }
  };

  handleActionEdit = (actionDto: IActionDefinitionDto) => {
    this.setState(
      {
        actionToEdit: actionDto,
        isCustomActionModalActive: true,
        refreshAction: false,
        isNewGenericSqlAction: false,
        genericSelectData: undefined
      },
      () => {
        if (actionDto.kDatasource_Id === this.state.activeDatasource.id) return;

        const ds = this.props.sd.datasources.find((ds) => ds.id === actionDto.kDatasource_Id);
        if (!ds) return;

        this.selectDatasource(ds, false, true);
      }
    );
  };

  getDatasourcesOfDomain = async () => {
    this.refreshActions();
  };

  handleSearchTextOnInit = () => {
    const searchText = this.props.sd.searchTexts[ViewPart.DatasourcesPanel];
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true, searchText });
    }
  };

  handleSearchFocus = (event) => event.target.select();

  deleteSearchText = (e: any) => {
    e.stopPropagation();
    this.props.sd.setSearchTexts(ViewPart.DatasourcesPanel, "");
    this.setState({ searchText: "", isSearchActive: false });
  };

  openSqlActionModal = (folderId?: string) => {
    if (this.props.sd.dashboardState === DashboardState.workFlow) {
      this.hideWorkflowContextPad();
    }
    this.setState({
      actionToEdit: {} as IActionDefinitionDto,
      isCustomActionModalActive: true,
      refreshAction: false,
      isNewGenericSqlAction: false,
      folderToAdd: folderId
    });
  };

  openSelectActionModal = () => {
    this.setState({
      actionToEdit: {} as IActionDefinitionDto,
      isCustomActionModalActive: true,
      refreshAction: false,
      isNewGenericSqlAction: true
    });
  };

  hideWorkflowContextPad = () => {
    const contextPad = document.getElementsByClassName("djs-context-pad");
    if (contextPad.length > 0) {
      contextPad[0].setAttribute("style", "display:none");
    }
  };

  openSqlActionModalForEdit = (action) => {
    this.setState({
      actionToEdit: action,
      isCustomActionModalActive: true,
      refreshAction: false
    });
  };

  refreshSelectedAction = (action) => {
    this.setState({
      actionToEdit: action,
      refreshAction: true,
      isCustomActionModalActive: true
    });
  };

  saveGenericSqlAction = async (): Promise<boolean> => {
    const { genericSelectData, actionToEdit, activeDatasource } = this.state;

    if (!actionToEdit?.name) {
      KMainFunctions.displayErrorNotification("Please enter an action name.");
      return false;
    }

    try {
      await this.nameValidator(null, actionToEdit.name);
    } catch (e: any) {
      KMainFunctions.displayErrorNotification(e.message);
      return false;
    }

    const statementReq = await DatasourceService.GetGenericSelectDataSql(
      activeDatasource.id,
      this.state.genericSelectData as IGenericSelectDataModel
    );

    // Service catches errors and returns the AxiosError object — guard against undefined .data
    // so we never call SaveAction with statement=undefined (root cause of KOA-17354).
    if (!statementReq?.data) {
      return false;
    }

    const action = {} as IActionDefinitionDto;
    action.kDatasource_Id = activeDatasource.id;
    action.name = actionToEdit.name;
    action.statement = statementReq.data;
    action.genericSelectData = genericSelectData || actionToEdit.genericSelectData;
    action.parameters = actionToEdit.parameters || [];

    if (actionToEdit.id) {
      action.id = actionToEdit.id;
    }

    try {
      const res = await DatasourceService.SaveAction(action);
      if (!res || res.status !== 200) {
        return false;
      }

      const resData = Array.isArray(res.data) ? res.data : [];
      const addedAction = resData.find((x) => x.name === action.name);
      const actionId = addedAction?.id;
      if (this.state.folderToAdd && actionId) {
        const folder = this.props.sd.getFolderById(this.state.folderToAdd, this.props.sd.project.actionFolders);
        if (folder && folder.parentFolderId) {
          this.props.sd.addActionToFolderInFolder(folder.parentFolderId, this.state.folderToAdd, actionId);
        } else {
          this.props.sd.addActionToFolder(this.state.folderToAdd, actionId);
        }
      }

      // The dialog shows its ✓ and then closes itself through `onSaved` (`handleGenericSqlSaved`).
      return true;
    } catch (e: any) {
      KMainFunctions.exceptionHandler(e);
      return false;
    }
  };

  /**
   * The select-action dialog's save has shown its ✓: close it, and light the row in the list
   * behind — asked for only now, so the highlight is not spent behind the closing dialog.
   */
  handleGenericSqlSaved = (): void => {
    const name: string | undefined = this.state.actionToEdit?.name;
    if (name) {
      requestDatasourceReveal({ name, parentId: this.state.activeDatasource.id });
    }
    this.closeCustomActionModal(true);
  };

  closeCustomActionModal = (shouldRefresh?: boolean) => {
    if (shouldRefresh) {
      this.refreshActions();
    }
    this.setState({
      isCustomActionModalActive: false,
      actionToEdit: {} as IActionDefinitionDto,
      isNewGenericSqlAction: false,
      genericSelectData: undefined
    });
    this.props.sd.setActionToEditOnActionPanel(undefined);
  };

  updateGenericSelectData = (data: IGenericSelectDataModel) => {
    this.setState({ genericSelectData: data });
  };

  convertGenericSelectToSql = async (sqlString: string) => {
    const { genericSelectData, actionToEdit } = this.state;
    const dataToUse = genericSelectData || actionToEdit?.genericSelectData;

    if (!dataToUse) return;

    const parameters = await KMainFunctions.calculateGenericSelectParameters(dataToUse);
    const newGenericSelectData = _.cloneDeep(dataToUse);
    newGenericSelectData.statementData = {
      createdAt: new Date(),
      isConvertedToSql: true,
      statement: sqlString,
      statementLanguage: EStatementLanguage.MsSql
    };

    const newActionToEdit = {
      ...this.state.actionToEdit,
      statement: sqlString,
      actionParameters: parameters,
      genericSelectData: newGenericSelectData
    };

    this.setState({
      genericSelectData: newGenericSelectData,
      actionToEdit: newActionToEdit,
      isNewGenericSqlAction: false,
      isCustomActionModalActive: true
    });

    this.props.sd.setGenericSelectActionCreatingStatus(false);
  };

  nameValidator = async (rule, value) => {
    const actions = this.props.sd.actionDefinitions.filter((x) => x.kDatasource_Id === this.state.activeDatasource?.id);
    if (this.state.actionToEdit?.id) {
      const filteredActions = actions.filter((x) => x.id !== this.state.actionToEdit.id);
      if (filteredActions.some((x) => x.name === value)) {
        return Promise.reject(new Error("Action name already exists"));
      }
    } else if (actions.some((x) => x.name === value)) {
      return Promise.reject(new Error("Action name already exists"));
    }
    return Promise.resolve();
  };

  openNewFolderModal = (parentFolderId?: string) => {
    if (this.state.isEditFolderModalActive) {
      this.setState({
        isEditFolderModalActive: false,
        folderParentFolderId: parentFolderId ?? null
      });
    }
    this.setState({
      isNewFolderModalActive: true,
      folderParentFolderId: parentFolderId ?? null
    });
  };

  closeNewFolderModal = () => {
    this.setState({ isNewFolderModalActive: false });
  };

  openEditFolderModal = (folderId: string, folderName: string, parentFolderId?: string) => {
    if (this.state.isNewFolderModalActive) {
      this.setState({ isNewFolderModalActive: false });
    }
    this.setState({
      isEditFolderModalActive: true,
      editingFolderId: folderId,
      editingFolderName: folderName,
      folderParentFolderId: parentFolderId ?? null
    });
  };

  closeEditFolderModal = () => {
    this.setState({
      isEditFolderModalActive: false,
      editingFolderId: "",
      editingFolderName: "",
      folderParentFolderId: ""
    });
  };

  refreshActions = async () => {
    this.setState({ isDatasourcesLoading: true });
    try {
      await this.props.sd.loadActionDefinitions();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isDatasourcesLoading: false });
      if (this.props.sd?.datasources && this.props.sd?.datasources?.length > 0 && !this.state.activeDatasource.id) {
        const managedDb = this.getManagedDB();
        this.selectDatasource(managedDb || this.props.sd.datasources[0], true);
      }
    }
  };

  selectDatasource = (ds: IDatasourceDto | undefined, doNotGetSchemas?: boolean, doNotResetActionToEdit?: boolean) => {
    if (!ds) return;
    const actions = this.props.sd.actionDefinitions.filter((x) => x.kDatasource_Id === ds?.id);
    if (!doNotResetActionToEdit) {
      this.setState({
        activeDatasource: ds,
        selectedDsCustomActions: actions,
        actionToEdit: {} as IActionDefinitionDto
      });
    } else {
      this.setState({ activeDatasource: ds, selectedDsCustomActions: actions });
    }
    if (
      ds.datasourceType !== EDatasourceType.AIActions &&
      ds.datasourceType !== EDatasourceType.Blockly &&
      ds.datasourceType !== EDatasourceType.JavaScriptAction &&
      !doNotGetSchemas
    )
      this.getSchema(ds);
  };

  openActionUsagesModal = (action: IActionDefinitionDto) => {
    this.setState({ actionToInspectUsages: action, isActionUsagesModalVisible: true });
  };

  openDeleteSqlActionModal = (action) => {
    this.setState({
      actionToDelete: action,
      isDeleteSqlActionModalVisible: true
    });
  };

  openDeleteActionFolderModal = (folder) => {
    this.setState({
      folderToDelete: folder,
      isDeleteActionFolderModalVisible: true
    });
  };

  deleteSqlAction = async () => {
    await this.props.sd.saveScreen(true);
    await DatasourceService.DeleteAction(this.state.actionToDelete)
      .then((data) => {
        if (data.status == 200) {
          this.refreshActions();
          KMainFunctions.displaySuccessNotification("Action deleted successfully");
        }
      })
      .catch((error) => {
        KMainFunctions.displayErrorNotification(error.response.data);
      });
  };

  getSchema = async (ds: IDatasourceDto) => {
    const treeData: Array<SchemaNode> = [];

    if (
      ds?.datasourceType !== EDatasourceType.CustomDb &&
      ds?.datasourceType !== EDatasourceType.Oracle &&
      ds?.datasourceType !== EDatasourceType.ManagedDbOracle &&
      ds?.datasourceType !== EDatasourceType.Postgre &&
      ds?.datasourceType !== EDatasourceType.ManagedDbPostgre &&
      ds?.datasourceType !== EDatasourceType.ManagedDb
    ) {
      this.setState({ schema: treeData });
      return;
    }
    try {
      const response = await DatasourceService.GetSchema(ds?.id);
      if (response.status === 200) {
        for (let index = 0; index < response.data.tables.length; index++) {
          const tbl = response.data.tables[index];
          treeData.push({
            title: tbl.name,
            key: tbl.id,
            children: tbl.columns.map((x) => {
              return { title: x.name, key: x.id, isLeaf: true, children: [], kuikaDataType: x.kuikaDataType };
            }),
            isLeaf: false
          });
        }
      }
    } catch {}
    this.setState({ schema: treeData });
  };

  onFolderPanelChange = (key) => {
    this.setState({ activeKeys: key });
  };

  ensureFoldersExpanded = (folderIds: (string | undefined)[]) => {
    const idsToOpen: string[] = folderIds.filter((id): id is string => Boolean(id));
    if (idsToOpen.length === 0) {
      return;
    }
    this.setState((prevState: IDataSourcesState) => {
      const next: Set<string> = new Set(prevState.activeKeys);
      idsToOpen.forEach((id: string) => next.add(id));
      return { activeKeys: Array.from(next) };
    });
  };

  // Strip RBD's placeholder from the value handed to KuikaCollapsePanel (which would render it as a
  // sibling *outside* the collapse, misplacing the reserved slot). We re-render it inside the folder
  // content instead, so the collapse grows and items below get pushed down rather than overlapped.
  withoutPlaceholder = (provided: any) => {
    return { ...provided, placeholder: null };
  };

  // Spring-loaded expand: only open a collapsed folder after the drag dwells over it briefly.
  // Expanding fires a setState that re-renders this (large) datasources panel, so doing it for every
  // folder the cursor merely passes over during a drag caused visible lag. A short dwell timer means
  // only the folder you intend to drop into pays that cost.
  expandHoverTimer: ReturnType<typeof setTimeout> | null = null;
  expandHoverTargetId: string | null = null;

  clearExpandHover = () => {
    if (this.expandHoverTimer) {
      clearTimeout(this.expandHoverTimer);
      this.expandHoverTimer = null;
    }
    this.expandHoverTargetId = null;
  };

  onDragUpdate = (update: any) => {
    const destId: string | undefined = update?.destination?.droppableId;
    if (!destId || destId === "droppable2") {
      this.clearExpandHover();
      return;
    }
    if (destId === this.expandHoverTargetId) {
      return;
    }
    this.clearExpandHover();
    const folder = this.props.sd.getFolderById(destId, this.props.sd.project.actionFolders);
    if (!folder) {
      return;
    }
    const parentId: string | undefined = folder.parentFolderId;
    const isAlreadyOpen: boolean =
      this.state.activeKeys.includes(destId) && (!parentId || this.state.activeKeys.includes(parentId));
    if (isAlreadyOpen) {
      return;
    }
    this.expandHoverTargetId = destId;
    this.expandHoverTimer = setTimeout(() => {
      this.ensureFoldersExpanded([parentId, destId]);
      this.expandHoverTimer = null;
    }, 350);
  };

  onDragEnd = (result: any) => {
    this.clearExpandHover();
    const { source, destination } = result;
    const folder = this.props.sd.getFolderById(
      destination?.droppableId !== "droppable2" && destination?.droppableId
        ? destination?.droppableId
        : source.droppableId,
      this.props.sd.project.actionFolders
    );
    if (!destination || destination?.droppableId === "droppable2") {
      if (folder?.parentFolderId) {
        this.props.sd.removeActionFromFolderInFolder(folder.parentFolderId, folder.id, result.draggableId);
      } else {
        this.props.sd.removeActionFromFolder(source.droppableId, result.draggableId);
      }
    } else if (folder?.parentFolderId) {
      this.props.sd.addActionToFolderInFolder(folder.parentFolderId, destination?.droppableId, result.draggableId);
      // Drop landed in a sub-folder — open it and its parent so the moved action is visible.
      this.ensureFoldersExpanded([folder.parentFolderId, destination?.droppableId]);
    } else {
      this.props.sd.addActionToFolder(destination?.droppableId, result.draggableId);
      // Drop landed in a (possibly collapsed) folder — auto-expand it to reveal the moved action.
      this.ensureFoldersExpanded([destination?.droppableId]);
    }
  };

  deleteFolder = () => {
    if (this.state.folderToDelete?.parentFolderId) {
      this.props.sd.deleteActionFolderInFolder(this.state.folderToDelete?.parentFolderId, this.state.folderToDelete.id);
    } else {
      this.props.sd.deleteActionFolder(this.state.folderToDelete?.id);
    }
  };

  getProviderBadge = (datasource: IDatasourceDto) => {
    return <DatasourceProviderBadge datasource={datasource} />;
  };

  getDataSourceName = (datasource: IDatasourceDto) => {
    if (datasource.datasourceType == EDatasourceType.ManagedDb) {
      return "Managed DB (SQL Server)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbOracle) {
      return "Managed DB (Oracle)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbPostgre) {
      return "Managed DB (Postgre)";
    }

    if (datasource.datasourceType == EDatasourceType.VisionAction) {
      return "Vision Actions";
    }

    return datasource.name;
  };

  findDatasourceIcon = (dbId: string) => {
    const datasource: IDatasourceDto | undefined = this.props.sd.datasources.find((ds) => ds?.id === dbId);
    const isMainAi = KMainFunctions.isMainAI();
    if (datasource?.connectionString === "") {
      if (isMainAi) {
        return <img src="/assets/main-ai/action.svg" />;
      }
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 7C0 3.70017 0 2.05025 1.02513 1.02513C2.05025 0 3.70017 0 7 0C10.2998 0 11.9497 0 12.9749 1.02513C14 2.05025 14 3.70017 14 7C14 10.2998 14 11.9497 12.9749 12.9749C11.9497 14 10.2998 14 7 14C3.70017 14 2.05025 14 1.02513 12.9749C0 11.9497 0 10.2998 0 7Z"
            fill="#A3B0CC"
          />
          <path
            d="M6.42665 11.2C6.17637 11.2 5.90196 11.1174 5.60946 10.9582C4.26156 10.2207 3.26043 9.09769 2.77192 8.29288C2.50053 7.8461 2.34373 7.50949 2.33468 7.10861V7.07189C2.33468 7.05047 2.33167 7.02599 2.33468 7.00457C2.33468 6.97397 2.33468 6.94949 2.33468 6.92807V6.89135C2.34675 6.48741 2.50355 6.1508 2.77494 5.70708C3.26344 4.90228 4.26457 3.77922 5.61248 3.04173C5.90498 2.8826 6.17938 2.79998 6.42966 2.79998C6.67995 2.79998 6.84278 2.87954 6.93928 2.92544L6.95435 2.93156C7.25891 3.07539 7.46396 3.28042 7.56046 3.54665L7.5695 3.56807C7.60267 3.65069 7.68409 3.84654 7.56347 4.13725C7.46095 4.38206 7.16543 4.66053 7.00863 4.80741C6.97244 4.84107 6.94229 4.86861 6.9242 4.89004C6.36332 5.46228 5.66374 6.17528 5.67279 7.00151C5.66072 7.82468 6.36031 8.53769 6.9242 9.10993C6.94531 9.13135 6.97244 9.15889 7.00863 9.19255C7.16845 9.3425 7.46095 9.6179 7.56347 9.86271C7.68409 10.1534 7.60569 10.3493 7.5695 10.4319L7.56046 10.4533C7.46095 10.7165 7.25891 10.9246 6.95435 11.0684L6.93928 11.0745C6.83977 11.1204 6.67392 11.2 6.42966 11.2H6.42665Z"
            fill="white"
          />
          <path
            d="M9.00184 9.71891C7.99468 9.50776 7.10814 8.57137 6.78549 8.02055C6.14621 6.92503 6.75835 6.00088 6.78549 5.96416C6.79755 5.9458 8.01278 4.20765 9.08024 4.20765C9.1104 4.20765 9.14055 4.20765 9.16769 4.21377C10.1115 4.33618 11.0041 5.32459 11.375 5.95804C12.0173 7.05355 11.4021 7.98383 11.375 8.02361C11.3629 8.03891 10.1568 9.71279 9.08929 9.72503C9.08929 9.72503 9.03501 9.72503 8.99883 9.71891H9.00184Z"
            fill="white"
          />
        </svg>
      );
    }
    if (datasource?.datasourceType === EDatasourceType.SAP) {
      return <KuikaSvg width={16} height={16} name="sap" type="datasource-icons" />;
    }
    if (
      datasource?.datasourceType === EDatasourceType.CustomDb ||
      datasource?.datasourceType === EDatasourceType.Oracle ||
      datasource?.datasourceType === EDatasourceType.Postgre ||
      datasource?.datasourceType === EDatasourceType.MsSql
    ) {
      return <KuikaSvg width={16} height={16} name="database" type="datasource-icons" />;
    }
    if (
      datasource?.datasourceType === EDatasourceType.RestApi ||
      datasource?.datasourceType === EDatasourceType.RestApiWOSwagger
    ) {
      return <KuikaSvg width={16} height={16} name="cloud-cog" type="datasource-icons" />;
    }
    if (
      datasource?.datasourceType === EDatasourceType.CSharpFunctions ||
      datasource?.datasourceType === EDatasourceType.CustomAuthenticationProvider ||
      datasource?.datasourceType === EDatasourceType.CustomAuthorizationProvider ||
      datasource?.datasourceType === EDatasourceType.CustomVerificationCodeSenderProvider ||
      datasource?.datasourceType === EDatasourceType.CustomConnectionStringProvider ||
      datasource?.datasourceType === EDatasourceType.CustomExternalTokenValidatorProvider ||
      datasource?.datasourceType === EDatasourceType.CustomMailProvider
    ) {
      return <KuikaSvg width={16} height={16} name="hexagon" type="datasource-icons" />;
    }
    if (datasource?.datasourceType === EDatasourceType.VisionAction) {
      return <KuikaSvg width={16} height={16} name="vision-action" type="datasource-icons" />;
    }
  };

  getFolderDropdown = (folder: IActionFolder) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={this.folderActionMenu(folder)} overlayClassName="kuika-screens__dropdown">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  folderActionMenu = (folder: IActionFolder) => {
    let parentFolder;
    if (folder.parentFolderId) {
      parentFolder = this.props.sd.project?.actionFolders?.find((x) => x.id === folder.parentFolderId);
    }

    const isExcludedDatasourceType = [
      EDatasourceType.RestApi,
      EDatasourceType.RestApiWOSwagger,
      EDatasourceType.CSharpFunctions,
      EDatasourceType.CustomAuthenticationProvider,
      EDatasourceType.CustomAuthorizationProvider,
      EDatasourceType.CustomVerificationCodeSenderProvider,
      EDatasourceType.CustomConnectionStringProvider,
      EDatasourceType.CustomMailProvider,
      EDatasourceType.Blockly,
      EDatasourceType.JavaScriptAction,
      EDatasourceType.PythonAction,
      EDatasourceType.AIActions,
      EDatasourceType.BleService,
      EDatasourceType.Mqtt
    ].includes(this.state.activeDatasource.datasourceType);

    const isAiAgent = this.state.activeDatasource.datasourceType === EDatasourceType.AIAgent;

    const menu: any = [
      !parentFolder && {
        label: "Add sub-folder",
        icon: <KuikaSvg width={16} height={16} name="folder-plus" type="sidebar-icons" />,
        onClick: () => this.openNewFolderModal(folder.id)
      },
      isAiAgent && {
        label: "AI Agent",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        onClick: () => this.handleOpenAiAgentModal(folder.id)
      },
      !isExcludedDatasourceType &&
        !isAiAgent && {
          label: "SQL Action",
          icon: <KuikaSvg width={16} height={16} name="plus" />,
          onClick: () => this.handleOpenActionModal(folder.id)
        },
      {
        label: "Rename",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        onClick: () => this.openEditFolderModal(folder.id, folder.name, folder?.parentFolderId ?? undefined)
      },
      {
        label: <span style={{ color: "var(--red-5)" }}>Delete</span>,
        icon: <KuikaSvg width={16} height={16} name="trash" color="var(--red-5)" />,
        onClick: () => this.openDeleteActionFolderModal(folder)
      }
    ].filter(Boolean);

    return menu;
  };

  actionMenu = (action) => {
    const isTemplateBased =
      action.actionType === EDatasourceType.AIActions &&
      !!this.props.sd.getStringifiedAiActionInfo(action.aiActionInfo)?.templateId;

    const menu: any = [
      {
        label: isTemplateBased ? (
          <KuikaTooltip content="Template-based AI actions cannot be edited" position="left">
            <span>Edit</span>
          </KuikaTooltip>
        ) : (
          "Edit"
        ),
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        disabled: isTemplateBased,
        onClick: () => {
          if (action.actionType === EDatasourceType.AIAgent) {
            const aiAgentDatasource = this.props.sd.datasources.find(
              (ds) => ds.datasourceType === EDatasourceType.AIAgent
            );
            if (aiAgentDatasource) {
              this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
              this.props.sd.setEditingActionsDatasource(aiAgentDatasource, action, false, false);
            }
          } else if (action.actionType === EDatasourceType.VisionAction) {
            const visionActionDatasource = this.props.sd.datasources.find(
              (ds) => ds.datasourceType === EDatasourceType.VisionAction
            );
            if (visionActionDatasource) {
              this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
              this.props.sd.setEditingActionsDatasource(visionActionDatasource, action, false, false);
            }
          } else {
            this.openSqlActionModalForEdit(action);
          }
        }
      },
      action?.actionType !== EDatasourceType.AIActions &&
        action?.actionType !== EDatasourceType.AIAgent &&
        action?.actionType !== EDatasourceType.Blockly &&
        !isInlineCodeDatasourceType(action?.actionType) && {
          label: "Refresh",
          icon: <KuikaSvg width={16} height={16} name="reload" />,
          onClick: () => this.refreshSelectedAction(action)
        },
      {
        label: "Where Used",
        icon: <KuikaSvg width={16} height={16} name="list-search" />,
        onClick: () => this.openActionUsagesModal(action)
      },
      {
        label: "Delete",
        // The kit's own danger row: --red-5 on the label and the glyph, in both themes.
        danger: true,
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        onClick: () => this.openDeleteSqlActionModal(action)
      }
    ].filter(Boolean);

    return menu;
  };

  getActionDropdown = (action) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={this.actionMenu(action)} overlayClassName="kuika-screens__dropdown">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  renderActionsList = (provided, snapshot, action) => {
    if (
      this.state.activeDatasource?.datasourceType === EDatasourceType.BleService &&
      endsWith(action.name, "_bluetooth")
    ) {
      return <></>;
    }
    const hasAccessManageCustomActions = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageCustomActions,
      this.props.sd.project?.projectAccess
    );

    const shouldShowDragHandle = this.props.sd.project.actionFolders && this.props.sd.project.actionFolders.length > 0;

    const isHighlighted = this.state.mcpHighlightActionName && action.name === this.state.mcpHighlightActionName;
    const highlightClass = isHighlighted
      ? this.state.mcpHighlightIsDelete
        ? "kuika-menu-item--mcp-highlight-delete"
        : "kuika-menu-item--mcp-highlight"
      : undefined;

    return (
      <KuikaMenuItem
        key={action.id}
        title={action.name}
        provided={provided}
        className={highlightClass}
        leftIcon={this.findDatasourceIcon(action.kDatasource_Id)}
        showDragHandle={shouldShowDragHandle}
        rightDropdown={
          this.state.activeDatasource.datasourceType !== EDatasourceType.RestApi &&
          this.state.activeDatasource.datasourceType != EDatasourceType.CSharpFunctions &&
          this.state.activeDatasource.datasourceType !== EDatasourceType.RestApiWOSwagger &&
          this.state.activeDatasource.datasourceType !== EDatasourceType.BleService &&
          this.state.activeDatasource.datasourceType !== EDatasourceType.Mqtt &&
          this.state.activeDatasource.datasourceType !== EDatasourceType.Blockly &&
          !isInlineCodeDatasourceType(this.state.activeDatasource.datasourceType) &&
          this.state.activeDatasource.datasourceType !== EDatasourceType.AIActions &&
          hasAccessManageCustomActions &&
          this.getActionDropdown(action)
        }
      />
    );
  };

  datasourceActionsMenu = () => {
    const { activeDatasource } = this.state;

    const isDatabaseType = [
      EDatasourceType.ManagedDb,
      EDatasourceType.ManagedDbOracle,
      EDatasourceType.Oracle,
      EDatasourceType.ManagedDbPostgre,
      EDatasourceType.Postgre,
      EDatasourceType.CustomDb
    ].includes(activeDatasource.datasourceType);

    const isAiAgent = activeDatasource.datasourceType === EDatasourceType.AIAgent;

    const menu: any = [
      isDatabaseType && {
        label: "SQL Action",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        onClick: () => this.handleOpenActionModal()
      },
      isDatabaseType && {
        label: "Select Action",
        icon: <KuikaSvg width={16} height={16} name="lightning" />,
        onClick: () => this.handleOpenSelectModal()
      },
      isAiAgent && {
        label: "AI Agent",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        onClick: () => this.handleOpenAiAgentModal()
      },
      {
        label: "New Folder",
        icon: <KuikaSvg width={16} height={16} name="folder-plus" type="sidebar-icons" />,
        onClick: () => this.openNewFolderModal(undefined)
      }
    ].filter(Boolean);

    return menu;
  };

  handleOpenActionModal = (folderId?: string) => {
    const accessName = "Actions";
    const actions = this.props.sd.actionDefinitions.filter(
      (action) =>
        action.actionType !== EDatasourceType.SystemJSFunctions &&
        action.actionType !== EDatasourceType.SystemCSFunctions
    );
    if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
      this.props.showDomainAccessRequiredModal(accessName);
    } else {
      this.openSqlActionModal(folderId);
    }
  };

  handleOpenSelectModal = () => {
    const accessName = "Actions";
    const actions = this.props.sd.actionDefinitions.filter(
      (action) =>
        action.actionType !== EDatasourceType.SystemJSFunctions &&
        action.actionType !== EDatasourceType.SystemCSFunctions
    );
    if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
      this.props.showDomainAccessRequiredModal(accessName);
    } else {
      this.openSelectActionModal();
    }
  };

  handleOpenAiAgentModal = (folderId?: string) => {
    const accessName = "AI Agents";
    const aiAgents = this.props.sd.actionDefinitions.filter((action) => action.actionType === EDatasourceType.AIAgent);
    if (KMainFunctions.isDomainHasAccess(accessName, aiAgents) === false) {
      this.props.showDomainAccessRequiredModal(accessName);
    } else {
      const aiAgentDatasource = this.props.sd.datasources.find((ds) => ds.datasourceType === EDatasourceType.AIAgent);
      if (aiAgentDatasource) {
        this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
        this.props.sd.setEditingActionsDatasource(
          aiAgentDatasource,
          undefined,
          true,
          false,
          folderId,
          KMainFunctions.defaultUniqueListElementNameGenerator(this.props.sd.actionDefinitions, "name", "AI_Agent")
        );
      }
    }
  };

  openDatasourcesTabForEdit = async (datasource: IDatasourceDto) => {
    await openDatasourceForEditing(getSdInstance(), datasource.id, {
      ensureView: true
    });
  };

  getManagedDB = () => {
    const managedDB = this.props.sd?.datasources?.find((d) => d.connectionString == "");
    if (managedDB) {
      return managedDB;
    }
  };

  handleSearch = (searchText: string) => {
    this.props.sd.setSearchTexts(ViewPart.DatasourcesPanel, searchText);
    this.setState({ searchText });
  };

  filterFolders = (folder) => {
    const { searchText } = this.state;
    const lowerCaseSearchText = KMainFunctions.turkishCharsToEnglish(searchText).toLowerCase();

    const folderMatches = folder.name.toLowerCase().includes(lowerCaseSearchText);

    if (folderMatches) {
      return true;
    }

    const folderActionMatches = folder.actionIds?.some((actionId) => {
      const actionX = this.state.selectedDsCustomActions.find((action) => action.id === actionId);
      return actionX?.name?.toLowerCase().includes(lowerCaseSearchText);
    });

    const childMatches = folder.children?.some((child) => this.filterFolders(child));

    return folderActionMatches || childMatches;
  };

  filterActions = (action) => {
    const { searchText } = this.state;
    const lowerCaseSearchText = KMainFunctions.turkishCharsToEnglish(searchText).toLowerCase();

    const parentFolder = this.props.sd.project.actionFolders?.find((folder) => folder.id === action.parentFolderId);
    if (parentFolder && parentFolder.name.toLowerCase().includes(lowerCaseSearchText)) {
      return true;
    }

    return action.name.toLowerCase().includes(lowerCaseSearchText);
  };

  render() {
    const { activeDatasource, isMenuOpen, searchText, isSearchActive, isDatasourcesLoading } = this.state;
    const actionsPresentation: LoadingPresentationBase = resolveLoadingPresentation(
      isDatasourcesLoading,
      (this.props.sd?.actionDefinitions?.length ?? 0) > 0
    );
    const selectedDsCustomActions = this.props.sd.actionDefinitions.filter(
      (x) => x.kDatasource_Id === activeDatasource.id
    );
    const managedDb = this.getManagedDB();
    const hasAccessManageCustomActions = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageCustomActions,
      this.props.sd.project.projectAccess
    );
    const iconName = iconMap[activeDatasource?.datasourceType] || "database";

    return (
      <>
        <DeleteModal
          itemName={this.state.actionToDelete.name}
          triggerModal={this.state.isDeleteSqlActionModalVisible}
          deleteItem={() => this.deleteSqlAction()}
          deleteType={"action"}
          closeModal={() => this.setState({ isDeleteSqlActionModalVisible: false })}
        />
        <ActionUsagesModal
          showModal={this.state.isActionUsagesModalVisible}
          closeModal={() => this.setState({ isActionUsagesModalVisible: false })}
          actionId={this.state.actionToInspectUsages?.id}
          actionName={this.state.actionToInspectUsages?.name}
        />
        <DeleteModal
          itemName={this.state.folderToDelete.name}
          triggerModal={this.state.isDeleteActionFolderModalVisible}
          deleteItem={() => this.deleteFolder()}
          deleteType={"folder"}
          closeModal={() => this.setState({ isDeleteActionFolderModalVisible: false })}
        />
        {this.state.isCustomActionModalActive && (
          <>
            {this.state.isNewGenericSqlAction ||
            (this.state.actionToEdit.genericSelectData &&
              !this.state.actionToEdit.genericSelectData.statementData?.isConvertedToSql) ? (
              <GenericSqlModal
                showModal={true}
                closeModal={() => this.closeCustomActionModal(false)}
                datasourceId={this.state.activeDatasource.id}
                nameValidator={this.nameValidator}
                genericSelectData={this.state.genericSelectData || this.state.actionToEdit.genericSelectData}
                updateGenericSelectData={this.updateGenericSelectData}
                convertToSql={this.convertGenericSelectToSql}
                onSubmit={() => this.saveGenericSqlAction()}
                onSaved={this.handleGenericSqlSaved}
                actionToEdit={this.state.actionToEdit}
                actionName={this.state.actionToEdit?.name}
                onActionNameChange={(name) => {
                  this.setState((prevState) => ({
                    actionToEdit: { ...prevState.actionToEdit, name }
                  }));
                }}
                startEditingName={!this.state.actionToEdit.id}
                workFlowList={this.props.sd.workFlowList}
                projectId={this.props.sd.project?.id}
              />
            ) : (
              <SqlActionModal
                schema={this.state.schema}
                datasource={this.state.activeDatasource}
                actionToEdit={this.state.actionToEdit}
                closeModal={(shouldRefresh?: boolean) => this.closeCustomActionModal(shouldRefresh)}
                refreshAction={this.state.refreshAction}
                sd={this.props.sd}
                folderToAdd={this.state.folderToAdd}
              />
            )}
          </>
        )}
        {this.state.isNewFolderModalActive && (
          <FolderCreateComponent
            showModal={this.state.isNewFolderModalActive}
            closeModal={this.closeNewFolderModal}
            dataSourceType={activeDatasource.id}
            folderParentFolderId={this.state.folderParentFolderId}
          ></FolderCreateComponent>
        )}
        {this.state.isEditFolderModalActive && (
          <FolderCreateComponent
            showEditModal={this.state.isEditFolderModalActive}
            editingFolderId={this.state.editingFolderId}
            editingFolderName={this.state.editingFolderName}
            closeEditModal={this.closeEditFolderModal}
            folderParentFolderId={this.state.folderParentFolderId}
          ></FolderCreateComponent>
        )}

        <div className="kuika-left-panel__container">
          <SearchablePanelHeader
            searchText={searchText}
            onSearchTextChange={this.handleSearch}
            onSearchClearCallback={this.deleteSearchText}
            viewPart={ViewPart.DatasourcesPanel}
            panelName={"Custom Actions"}
            isSearchActive={isSearchActive}
            setSearchActive={(value: boolean) => this.setState({ isSearchActive: value })}
          />

          <KuikaDropdownMenu
            header="Select Datasource"
            options={this.props.sd?.datasources
              ?.filter((ds) => {
                if (
                  KMainFunctions.isMainAI() &&
                  (ds.datasourceType === EDatasourceType.AIAgent || ds.datasourceType === EDatasourceType.AIActions)
                ) {
                  return false;
                }

                return true;
              })
              ?.sort((x, y) => {
                if (x.id === managedDb?.id) return -1;
                if (y.id === managedDb?.id) return 1;
                return 0;
              })
              ?.map((ds) => {
                return {
                  key: ds.id,
                  label: ds.isStaleRestApiClient ? (
                    // `datasourceListItem-<name>` so a test can assert a datasource is IN the
                    // list by id instead of by an `//*[text()='<name>']` XPath that matches any
                    // element on the page carrying that text (QA-484, scenario 82). Named like
                    // its siblings on this row, `datasourceStaleBadge-<name>` /
                    // `datasourceProviderBadge-<name>` — note those two render conditionally
                    // (the provider badge only for custom-provider types), so neither can serve
                    // as a general "this datasource exists" handle.
                    <span
                      data-testid={`datasourceListItem-${ds.name}`}
                      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
                    >
                      <KuikaOverflowText>{this.getDataSourceName(ds)}</KuikaOverflowText>
                      {this.getProviderBadge(ds)}
                      <KuikaTooltip content="Client code is outdated — re-save this datasource to apply the new REST API connection format.">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 16,
                            height: 16,
                            borderRadius: "var(--radius-pill)",
                            background: "var(--yellow-5)",
                            color: "var(--black)",
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: 1
                          }}
                          data-testid={`datasourceStaleBadge-${ds.name}`}
                        >
                          !
                        </span>
                      </KuikaTooltip>
                    </span>
                  ) : (
                    <span
                      data-testid={`datasourceListItem-${ds.name}`}
                      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
                    >
                      <KuikaOverflowText>{this.getDataSourceName(ds)}</KuikaOverflowText>
                      {this.getProviderBadge(ds)}
                    </span>
                  ),
                  icon: (
                    <KuikaSvg
                      name={iconMap[ds.datasourceType] || "database"}
                      type="datasource-icons"
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => this.selectDatasource(ds),
                  rightSlot: (
                    <KuikaSmallMediumText>
                      {this.props.sd.actionDefinitions.filter((x) => x.kDatasource_Id === ds.id)?.length || 0}
                    </KuikaSmallMediumText>
                  ),
                  showSelectedIcon: true
                };
              })}
            selected={activeDatasource?.id}
            setSelected={(id) => this.selectDatasource(this.props.sd?.datasources?.find((ds) => ds.id === id))}
            open={isMenuOpen}
            setOpen={(value) => this.setState({ isMenuOpen: value })}
            offset={[0, 8]}
            matchTriggerWidth
          >
            <KuikaMenuItem
              title={
                <span
                  style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
                  data-testid={`datasourceActiveName-${this.getDataSourceName(activeDatasource)}`}
                >
                  <KuikaText
                    className="kuika-datasource-panel__active-name"
                    title={this.getDataSourceName(activeDatasource)}
                  >
                    <KuikaOverflowText>{this.getDataSourceName(activeDatasource)}</KuikaOverflowText>
                  </KuikaText>
                  {this.getProviderBadge(activeDatasource)}
                  {activeDatasource?.isStaleRestApiClient && (
                    <KuikaTooltip content="Client code is outdated — re-save this datasource to apply the new REST API connection format.">
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 16,
                          height: 16,
                          flexShrink: 0,
                          borderRadius: "var(--radius-pill)",
                          background: "var(--yellow-5)",
                          color: "var(--black)",
                          fontSize: 10,
                          fontWeight: 700,
                          lineHeight: 1
                        }}
                        data-testid={`datasourceStaleBadgeActive-${activeDatasource.name}`}
                      >
                        !
                      </span>
                    </KuikaTooltip>
                  )}
                </span>
              }
              leftIcon={<KuikaSvg name={iconName} type="datasource-icons" width={16} height={16} />}
              rightUnit={
                <>
                  <KuikaButton
                    isIcon
                    type="tertiary"
                    size="sm"
                    id="datasource-dropdown"
                    color="neutral"
                    active={isMenuOpen}
                  >
                    <KuikaSvg
                      type="arrows"
                      name={isMenuOpen ? "chevron-up" : "chevron-down"}
                      width={16}
                      height={16}
                      pointer
                    />
                  </KuikaButton>

                  {hasAccessManageCustomActions && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <KuikaDropdownMenu options={this.datasourceActionsMenu()}>
                        {(open) => (
                          <KuikaButton isIcon type="tertiary" color="neutral" size="sm" active={open}>
                            <KuikaSvg name={"plus"} width={16} height={16} pointer />
                          </KuikaButton>
                        )}
                      </KuikaDropdownMenu>
                    </div>
                  )}
                </>
              }
            />
          </KuikaDropdownMenu>
          <div
            className="kuika-left-panel__content kuika-left-panel__content--custom-actions"
            // 85px of shell chrome + this panel's own 36px action-folder row (was 150px,
            // which also reserved the removed 24px footer).
            style={{ height: "calc(100vh - 121px - var(--panel-strip-growth, 0px))" }}
          >
            {/* The first load previews the list; a reload keeps the actions already on screen.
                  The header and the datasource picker above stay usable either way — only the list
                  is waiting. */}
            {actionsPresentation === "skeleton" ? (
              <KuikaSkeletonRegion label="Loading actions">
                <KuikaSkeletonList rows={6} density="compact" />
              </KuikaSkeletonRegion>
            ) : (
              <DragDropContext onDragEnd={this.onDragEnd} onDragUpdate={this.onDragUpdate}>
                {this.props.sd.project.actionFolders
                  ?.filter(this.filterFolders)
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  ?.map(
                    (folder) =>
                      folder.dataSourceType === activeDatasource.id && (
                        <Droppable droppableId={folder.id} key={folder.id}>
                          {(provided, snapshot) => (
                            <>
                              <KuikaCollapsePanel
                                provided={this.withoutPlaceholder(provided)}
                                activeKey={!(searchText.length > 0) ? this.state.activeKeys : [folder.id]}
                                onChange={this.onFolderPanelChange}
                                forceRender
                                className={`kuika-left-panel__folder-collapse kuika-collapse-panel${
                                  snapshot.isDraggingOver ? " kuika-left-panel__folder-collapse--drag-over" : ""
                                }`}
                                expandIcon={({ isActive }) =>
                                  isActive ? (
                                    <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
                                  ) : (
                                    <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
                                  )
                                }
                                items={[
                                  {
                                    label: (
                                      <div className="kuika-flex-center--gap">
                                        <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />
                                        <KuikaText>{folder.name}</KuikaText>
                                      </div>
                                    ),
                                    key: folder.id,
                                    extra: this.getFolderDropdown(folder),
                                    children: (
                                      <>
                                        {folder.actionIds && folder.actionIds.length > 0 ? (
                                          selectedDsCustomActions
                                            ?.filter(this.filterActions)
                                            ?.filter((action) => folder.actionIds?.includes(action.id))
                                            ?.map((action, index) => (
                                              <Draggable
                                                key={action.id}
                                                draggableId={action.id}
                                                index={index}
                                                isDragDisabled={!folder.actionIds || folder?.actionIds?.length === 0}
                                              >
                                                {(provided, snapshot) =>
                                                  this.renderActionsList(provided, snapshot, action)
                                                }
                                              </Draggable>
                                            ))
                                        ) : activeDatasource.datasourceType === EDatasourceType.AIAgent ? (
                                          <>
                                            <KuikaEmptyState
                                              svgName="lightning"
                                              name={"AI Agent"}
                                              description={"and add your first ever AI Agent."}
                                              onDescriptionClick={(e) => this.handleOpenAiAgentModal(folder.id)}
                                            />
                                          </>
                                        ) : activeDatasource.datasourceType === EDatasourceType.AIActions ||
                                          activeDatasource.datasourceType === EDatasourceType.Blockly ||
                                          isInlineCodeDatasourceType(activeDatasource.datasourceType) ||
                                          activeDatasource.datasourceType === EDatasourceType.Mqtt ||
                                          activeDatasource.datasourceType === EDatasourceType.BleService ||
                                          activeDatasource.datasourceType === EDatasourceType.RestApi ||
                                          activeDatasource.datasourceType === EDatasourceType.CSharpFunctions ||
                                          activeDatasource.datasourceType === EDatasourceType.RestApiWOSwagger ? (
                                          <>
                                            <KuikaEmptyState
                                              svgName="lightning"
                                              name={"Action"}
                                              description={"and add your first ever action."}
                                              onDescriptionClick={(e) =>
                                                this.props.sd.setDashboardState(DashboardState.dataDiagramScheme)
                                              }
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <KuikaEmptyState
                                              svgName="lightning"
                                              name={"SQL Action"}
                                              description={"and add your first ever action."}
                                              onDescriptionClick={(e) => this.handleOpenActionModal(folder.id)}
                                            />
                                          </>
                                        )}
                                        {folder.children &&
                                          folder.children.length > 0 &&
                                          folder.children.map((childFolder) => (
                                            <Droppable droppableId={childFolder.id} key={childFolder.id}>
                                              {(_provided, snapshot) => (
                                                <KuikaCollapsePanel
                                                  provided={this.withoutPlaceholder(_provided)}
                                                  activeKey={
                                                    !(searchText.length > 0) ? this.state.activeKeys : [childFolder.id]
                                                  }
                                                  onChange={this.onFolderPanelChange}
                                                  className={`kuika-left-panel__folder-collapse kuika-collapse-panel${
                                                    snapshot.isDraggingOver
                                                      ? " kuika-left-panel__folder-collapse--drag-over"
                                                      : ""
                                                  }`}
                                                  expandIcon={({ isActive }) =>
                                                    isActive ? (
                                                      <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
                                                    ) : (
                                                      <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
                                                    )
                                                  }
                                                  forceRender
                                                  items={[
                                                    {
                                                      label: (
                                                        <div className="kuika-flex-center--gap">
                                                          <KuikaSvg
                                                            name="folder"
                                                            type="sidebar-icons"
                                                            width={16}
                                                            height={16}
                                                          />
                                                          <KuikaText>{childFolder.name}</KuikaText>
                                                        </div>
                                                      ),
                                                      key: childFolder.id,
                                                      extra: this.getFolderDropdown(childFolder),
                                                      children: (
                                                        <>
                                                          {childFolder.actionIds && childFolder.actionIds.length > 0 ? (
                                                            selectedDsCustomActions
                                                              ?.filter(this.filterActions)
                                                              ?.filter((action) =>
                                                                childFolder.actionIds?.includes(action.id)
                                                              )
                                                              ?.map((action, index) => (
                                                                <Draggable
                                                                  key={action.id}
                                                                  draggableId={action.id}
                                                                  index={index}
                                                                  isDragDisabled={
                                                                    !this.props.sd.project.actionFolders ||
                                                                    this.props.sd.project.actionFolders?.length === 0
                                                                  }
                                                                >
                                                                  {(provided, snapshot) =>
                                                                    this.renderActionsList(provided, snapshot, action)
                                                                  }
                                                                </Draggable>
                                                              ))
                                                          ) : activeDatasource.datasourceType ===
                                                            EDatasourceType.AIAgent ? (
                                                            <KuikaEmptyState
                                                              svgName="lightning"
                                                              name={"AI Agent"}
                                                              description={"and add your first ever AI Agent."}
                                                              onDescriptionClick={(e) =>
                                                                this.handleOpenAiAgentModal(childFolder.id)
                                                              }
                                                            />
                                                          ) : (
                                                            <KuikaEmptyState
                                                              svgName="lightning"
                                                              name={"Action"}
                                                              description={"and add your first ever action."}
                                                              onDescriptionClick={(e) =>
                                                                this.handleOpenActionModal(childFolder.id)
                                                              }
                                                            />
                                                          )}
                                                          {/* drop slot inside sub-folder content so it grows */}
                                                          {_provided.placeholder}
                                                        </>
                                                      )
                                                    }
                                                  ]}
                                                />
                                              )}
                                            </Droppable>
                                          ))}
                                        {/* drop slot inside folder content so it grows and items below are pushed down */}
                                        {provided.placeholder}
                                      </>
                                    )
                                  }
                                ]}
                              />
                            </>
                          )}
                        </Droppable>
                      )
                  )}
                <Droppable droppableId="droppable2">
                  {(provided) => (
                    <div ref={provided.innerRef}>
                      {selectedDsCustomActions
                        ?.filter(this.filterActions)
                        ?.filter((x) => !this.props.sd.project.actionFolders?.find((y) => y.actionIds?.includes(x.id)))
                        ?.filter((x) => {
                          const folder = this.props.sd.findFolderByActionId(x.id, this.props.sd.project.actionFolders);
                          if (folder) {
                            return false;
                          }
                          return true;
                        })
                        ?.sort((a, b) => a.name.localeCompare(b.name))
                        .map((action, index) => (
                          <Draggable
                            key={action.id}
                            draggableId={action.id}
                            index={index}
                            isDragDisabled={
                              !this.props.sd.project.actionFolders ||
                              this.props.sd.project.actionFolders?.length === 0 ||
                              !this.props.sd.project.actionFolders?.some(
                                (x) => x.dataSourceType === activeDatasource?.id
                              )
                            }
                          >
                            {(provided, snapshot) => this.renderActionsList(provided, snapshot, action)}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

                {searchText.length > 0 &&
                  selectedDsCustomActions?.length > 0 &&
                  activeDatasource.datasourceType !== EDatasourceType.AIActions &&
                  activeDatasource.datasourceType !== EDatasourceType.Blockly &&
                  !isInlineCodeDatasourceType(activeDatasource.datasourceType) &&
                  activeDatasource.datasourceType !== EDatasourceType.Mqtt &&
                  activeDatasource.datasourceType !== EDatasourceType.BleService &&
                  activeDatasource.datasourceType !== EDatasourceType.RestApi &&
                  activeDatasource.datasourceType !== EDatasourceType.CSharpFunctions &&
                  activeDatasource.datasourceType !== EDatasourceType.RestApiWOSwagger &&
                  !this.props.sd.project.actionFolders?.some(this.filterFolders) &&
                  !selectedDsCustomActions?.some(this.filterActions) && (
                    <KuikaEmptyState
                      svgName="search-off"
                      customName="No Action found"
                      description="We couldn't find any items matching your search."
                    />
                  )}

                {selectedDsCustomActions?.length === 0 &&
                  activeDatasource.datasourceType === EDatasourceType.AIAgent && (
                    <KuikaEmptyState
                      svgName="lightning"
                      name={"AI Agent"}
                      description={"and add your first ever AI Agent."}
                      onDescriptionClick={() => this.handleOpenAiAgentModal()}
                    />
                  )}
                {selectedDsCustomActions?.length === 0 &&
                  activeDatasource.datasourceType !== EDatasourceType.AIAgent &&
                  activeDatasource.datasourceType !== EDatasourceType.AIActions &&
                  activeDatasource.datasourceType !== EDatasourceType.Blockly &&
                  !isInlineCodeDatasourceType(activeDatasource.datasourceType) &&
                  activeDatasource.datasourceType !== EDatasourceType.Mqtt &&
                  activeDatasource.datasourceType !== EDatasourceType.BleService &&
                  activeDatasource.datasourceType !== EDatasourceType.RestApi &&
                  activeDatasource.datasourceType !== EDatasourceType.CSharpFunctions &&
                  activeDatasource.datasourceType !== EDatasourceType.RestApiWOSwagger && (
                    <KuikaEmptyState
                      svgName="lightning"
                      name={"action"}
                      description={
                        <KuikaSmallText>
                          Add an{" "}
                          <KuikaSmallText
                            className="kuika-empty-state__description--link"
                            onClick={() => this.handleOpenActionModal()}
                          >
                            SQL Action
                          </KuikaSmallText>{" "}
                          or{" "}
                          <KuikaSmallText
                            className="kuika-empty-state__description--link"
                            onClick={() => this.handleOpenSelectModal()}
                          >
                            Select action
                          </KuikaSmallText>
                        </KuikaSmallText>
                      }
                    />
                  )}
                {selectedDsCustomActions?.length === 0 &&
                  (activeDatasource.datasourceType === EDatasourceType.AIActions ||
                    activeDatasource.datasourceType === EDatasourceType.Blockly ||
                    isInlineCodeDatasourceType(activeDatasource.datasourceType) ||
                    activeDatasource.datasourceType === EDatasourceType.Mqtt ||
                    activeDatasource.datasourceType === EDatasourceType.BleService ||
                    activeDatasource.datasourceType === EDatasourceType.RestApi ||
                    activeDatasource.datasourceType === EDatasourceType.CSharpFunctions ||
                    activeDatasource.datasourceType === EDatasourceType.RestApiWOSwagger) && (
                    <KuikaEmptyState
                      svgName="lightning"
                      name={"Action"}
                      description={"and add your first ever action."}
                      onDescriptionClick={(e) => this.props.sd.setDashboardState(DashboardState.dataDiagramScheme)}
                    />
                  )}
              </DragDropContext>
            )}
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const dataSources = connect(
  null,
  mapDispatchToProps
)(withContext<IDataSourcesProps>(withSD<IDataSourcesProps>(DataSources)));
export { dataSources as DataSources };
